import { FormEvent, useState } from "react";
import { Link } from "react-router-dom";

import { Logo } from "../../components/Logo";
import { SiteFooter } from "../../components/SiteFooter";
import { api, extractErrorMessage } from "../../lib/api";
import { setToken } from "../../lib/auth";

interface LoginResponse {
  ok: boolean;
  token: string;
}

export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setError("Enter email and password.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await api.post<LoginResponse>("/api/auth/login", { email, password });
      if (res.data.token) {
        setToken(res.data.token);
        // Full reload so the legacy /dashboard route picks up the new token
        // until PR 5 migrates the dashboard into this SPA.
        window.location.href = "/dashboard";
        return;
      }
      setError("Login failed");
      setSubmitting(false);
    } catch (err) {
      // The legacy backend returns 403 when the account is unverified or
      // unapproved; route the user to /pending so they know to check email.
      if (
        err &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status === 403
      ) {
        window.location.href = "/pending";
        return;
      }
      setError(extractErrorMessage(err, "Login failed"));
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="top-line" />
      <div className="page">
        <form className="card" onSubmit={onSubmit} noValidate>
          <Logo />
          <h1>Sign In</h1>
          <p className="sub">Manage your screens and content.</p>
          {error && (
            <div className="error-msg" role="alert">
              {error}
            </div>
          )}
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className="form-input"
              type="email"
              placeholder="you@company.com"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              className="form-input"
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="btn btn--primary btn--block"
            disabled={submitting}
          >
            {submitting ? "Signing in..." : "Sign In"}
          </button>
          <p className="alt-link">
            Need access? <Link to="/register">Request an account</Link>
          </p>
        </form>
      </div>
      <SiteFooter />
    </>
  );
}
